import { isHandheldTerminal } from './useHardwareScanner';

/**
 * Which reader the operator is scanning with.
 *
 * These are modes rather than two readers running at once because on a rugged handheld they
 * cannot both be live: the browser holding the camera stops the device's own scan engine
 * firing. Trying to support both simultaneously produced a screen where neither worked
 * predictably, so the choice is now explicit and the operator makes it.
 */
export const SCAN_MODE = {
  /** Scandit on the device camera — the original behaviour, and the default off-terminal. */
  CAMERA: 'camera',
  /** The device's own scan engine, read through the keyboard wedge. No camera is started. */
  LASER: 'laser'
} as const;

export type ScanMode = (typeof SCAN_MODE)[keyof typeof SCAN_MODE];

const SCAN_MODE_PREFERENCE = 'rxerp.scanner.mode';

/**
 * The operator's saved choice, or a sensible first guess: a rugged terminal has a trigger and
 * usually a poor camera, so it starts in laser mode; a phone starts on the camera, which is what
 * every existing user already has.
 */
export const readScanMode = (): ScanMode => {
  try {
    const saved = window.localStorage.getItem(SCAN_MODE_PREFERENCE);
    if (saved === SCAN_MODE.CAMERA || saved === SCAN_MODE.LASER) return saved;
  } catch {
    // A storage we cannot read just means we fall through to the guess.
  }
  return isHandheldTerminal() ? SCAN_MODE.LASER : SCAN_MODE.CAMERA;
};

/** Remembers the choice across sessions, so an operator sets it once per device. */
export const persistScanMode = (mode: ScanMode) => {
  try {
    window.localStorage.setItem(SCAN_MODE_PREFERENCE, mode);
  } catch {
    /* applied for this session even if it cannot be persisted */
  }
};
